import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {SaveAlt} from '@mui/icons-material';
import {IconButton, Typography} from '@mui/material';
import moment from 'moment';
import {Attachment} from '../../../../../../../../../backend/interfaces/attachment';
import {UserAvatar} from '../../../../../planner/calendar/shared/user-avatar';
import {ComboActions} from '../../../../../../../../../components/combo-actions/combo-actions';
import {translations} from '../../../../../../../../../i18n/translations';
import {Pallete} from '../../../../../../../../../theme/bluebird';
import {UserPublicInfo} from '../../../../../../../../../backend/responses/user-public-info';
import {Table} from '../../../../../../../../../components/table/table';
import {formatBytes} from '../../../../../../../../../utils/data/format-bytes';

type SeniorAttachmentsTableItemProps = {
  item: Attachment;
  author?: UserPublicInfo;
  canWrite?: boolean;
  onEditClick: (attachment: Attachment) => void;
  onDeleteClick: (attachment: Attachment) => void;
  onDownloadClick: (attachment: Attachment) => void;
};

export function SeniorAttachmentsTableItem(props: SeniorAttachmentsTableItemProps) {
  const {t} = useTranslation();

  const formattedDate = useMemo(
    () => moment(props.item.createdAt).format('L LT'),
    [props.item.createdAt]
  );

  return (
    <Table.Row
      onClick={() => {
        props.onEditClick(props.item);
      }}
    >
      {/* ------------ NAME ------------ */}
      <Table.LabelCell>{props.item.name}</Table.LabelCell>

      {/* ------------ DESCRIPTION ------------ */}
      <Table.LabelCell>{props.item.description || '-'}</Table.LabelCell>

      {/* ------------ AUTHOR USER ------------ */}
      <Table.Cell>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <UserAvatar size="l" name={props.author?.name ?? '-'} picture={props.author?.picture} />
          <Typography>{props.author?.name}</Typography>
        </div>
      </Table.Cell>

      {/* ------------ DATE ------------ */}
      <Table.LabelCell>{formattedDate}</Table.LabelCell>

      {/* ------------ SIZE ------------ */}
      <Table.LabelCell>
        {props.item.sizeInMb ? formatBytes(props.item.sizeInMb * 1024 * 1024) : '-'}
      </Table.LabelCell>

      {/* ------------ OPTIONS ------------ */}
      <Table.Cell>
        <IconButton
          disableRipple
          aria-label="settings"
          sx={{
            width: '40px',
            height: '42px',
            '& .MuiSvgIcon-root': {
              fontSize: '32px',
            },
          }}
        >
          <ComboActions
            wrapperSX={{color: Pallete.gray.light}}
            triggerSX={{height: '32px', width: '32px'}}
            actions={[
              {
                label: t(translations.user_attachments.list.actions.edit),
                icon: {element: FiEdit},
                disabled: !props.canWrite,
                disabledTooltip: t(translations.user_attachments.access_denied.edit),
                callback: () => {
                  props.onEditClick(props.item);
                },
              },
              {
                label: t(translations.user_attachments.list.actions.delete),
                icon: {element: FiTrash2},
                disabled: !props.canWrite,
                disabledTooltip: t(translations.user_attachments.access_denied.delete),
                callback: () => {
                  props.onDeleteClick(props.item);
                },
              },

              {
                label: t(translations.user_attachments.list.actions.download),
                icon: {element: SaveAlt},
                callback: () => {
                  props.onDownloadClick(props.item);
                },
              },
            ]}
          />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}
